import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Link, useParams, useHistory } from "react-router-dom";
import axios from "axios";
import { API_URL } from "../../utils/constant.js";
import Pagination from "../Pagination/Pagination";
import { toastr } from "react-redux-toastr";

const Scans = () => {
  const { orgId } = useParams();
  const history = useHistory();
  const id = useSelector((state) => {
    return orgId
      ? orgId
      : state?.summary?.localClient?._id || state?.summary?.clients?.[0]?._id;
  });

  // VIP Members state (for dropdown)
  const [vipMembers, setVipMembers] = useState([]);
  const [loadingMembers, setLoadingMembers] = useState(false);

  // Scans state
  const [scans, setScans] = useState([]);
  const [loadingScans, setLoadingScans] = useState(false);
  const [showCreateScanModal, setShowCreateScanModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [newScan, setNewScan] = useState({
    vipMemberId: "",
    modules: [],
  });
  const [availableModules] = useState([
    { label: "Social Media", value: "social" },
    { label: "Data Breaches", value: "leaks" },
    { label: "Open Web", value: "crawling" },
  ]);

  const vendorName = sessionStorage.getItem("vendorName");
  const vendorId = sessionStorage.getItem("vendorId");

  // Fetch VIP Members (for dropdown)
  const fetchVipMembers = async () => {
    if (!id) return;
    setLoadingMembers(true);
    try {
      axios.defaults.headers.common["Authorization"] =
        `Bearer ${localStorage.getItem("session")}`;
      const response = await axios.get(
        `${API_URL}/executive-monitoring/${id}/vip-members`,
      );
      setVipMembers(
        Array.isArray(response.data?.data) ? response.data.data : [],
      );
    } catch (error) {
      console.error("Error fetching VIP members:", error);
      setVipMembers([]);
    } finally {
      setLoadingMembers(false);
    }
  };

  // Fetch Scans
  const fetchScans = async () => {
    if (!id) return;
    setLoadingScans(true);
    try {
      axios.defaults.headers.common["Authorization"] =
        `Bearer ${localStorage.getItem("session")}`;

      // Build query parameters for pagination
      const queryParams = new URLSearchParams();
      queryParams.append("page", currentPage.toString());
      queryParams.append("limit", pageSize.toString());

      const queryString = queryParams.toString();
      const url = `${API_URL}/executive-monitoring/${id}/scans?${queryString}`;

      const response = await axios.get(url);
      const responseData = response.data?.data;

      // Extract pagination metadata from API response
      const paginationInfo =
        response.data?.pagination || responseData?.pagination || {};
      const total = paginationInfo.totalCount || paginationInfo.total || 0;

      setTotalCount(total);
      setScans(Array.isArray(responseData?.scans) ? responseData.scans : []);
    } catch (error) {
      console.error("Error fetching scans:", error);
      setScans([]);
      setTotalCount(0);
    } finally {
      setLoadingScans(false);
    }
  };

  // Create Scan
  const createScan = async () => {
    if (!id || !newScan.vipMemberId || newScan.modules.length === 0) {
      toastr.error(
        "Error",
        "Please select a VIP member and at least one module",
      );
      return;
    }
    try {
      axios.defaults.headers.common["Authorization"] =
        `Bearer ${localStorage.getItem("session")}`;
      const response = await axios.post(
        `${API_URL}/executive-monitoring/${id}/scan`,
        newScan,
      );
      if (response.data) {
        setShowCreateScanModal(false);
        setNewScan({ vipMemberId: "", modules: [] });
        fetchScans();
        toastr.success("Success", "Scan initiated successfully");
      }
    } catch (error) {
      console.error("Error creating scan:", error);
      toastr.error(
        "Error",
        error.response?.data?.userMessage || "Failed to initiate scan",
      );
    }
  };

  // Toggle module selection
  const toggleModule = (module) => {
    setNewScan((prev) => ({
      ...prev,
      modules: prev.modules.includes(module)
        ? prev.modules.filter((m) => m !== module)
        : [...prev.modules, module],
    }));
  };

  // View scan results - navigate to issues page
  const viewScanResults = (scanId) => {
    history.push(`/executive-monitoring/issues?scanId=${scanId}`);
  };

  useEffect(() => {
    if (id) {
      fetchVipMembers();
      fetchScans();
    }
  }, [id, currentPage, pageSize]);

  return (
    <div className="container-fluid">
      <div className="block-header">
        <div className="row clearfix">
          <div className="col-md-6 col-sm-12">
            <h1 className="welcome-text">Executive Monitoring Scans</h1>
            <nav aria-label="breadcrumb" className="mt-1">
              <ol className="breadcrumb">
                <li className="breadcrumb-item">
                  {vendorId ? (
                    <Link to="/vendor" onClick={() => sessionStorage.clear()}>
                      Forest View
                    </Link>
                  ) : (
                    <Link to="/" className="welcome-textSmall">
                      Home
                    </Link>
                  )}
                </li>
                {vendorName && (
                  <li
                    className="breadcrumb-item active"
                    aria-current="page"
                    style={{ color: "white" }}
                  >
                    {vendorName}
                  </li>
                )}
                <li
                  className="breadcrumb-item active"
                  aria-current="page"
                  style={{ color: "white" }}
                >
                  Scans
                </li>
              </ol>
              <br />
            </nav>
          </div>
        </div>
      </div>

      {/* Scans Section */}
      <div className="card mb-4">
        <div className="body">
          <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap">
            <div className="d-flex align-items-center gap-3 flex-wrap mb-2 mb-md-0">
              <h6
                className="welcome-textSmall mb-0"
                style={{ fontSize: "14px" }}
              >
                Scans ({scans.length} of {totalCount})
              </h6>
            </div>
            <div className="d-flex gap-2">
              <button
                className="btn btn-primary btn-sm"
                onClick={fetchScans}
                disabled={loadingScans}
                style={{ fontSize: "12px" }}
              >
                <i
                  className={`fa ${loadingScans ? "fa-spinner fa-spin" : "fa-refresh"} mr-1`}
                ></i>
                {loadingScans ? "Loading..." : "Refresh"}
              </button>
              <button
                className="btn btn-primary btn-sm"
                onClick={() => setShowCreateScanModal(true)}
                style={{ fontSize: "12px" }}
              >
                <i className="fa fa-plus mr-1"></i>Initiate Scan
              </button>
            </div>
          </div>

          {/* Scans Table */}
          {loadingScans ? (
            <div className="text-center p-4">
              <i className="fa fa-spinner fa-spin"></i> Loading scans...
            </div>
          ) : (
            <>
              <div className="table-responsive">
                <table className="table table-custom spacing5 mb-0">
                  <thead>
                    <tr>
                      <th>Scan ID</th>
                      <th>VIP Member ID</th>
                      <th>VIP Member Name</th>
                      <th>Modules</th>
                      <th>Status</th>
                      <th>Created At</th>
                    </tr>
                  </thead>
                  <tbody>
                    {scans.length === 0 ? (
                      <tr>
                        <td colSpan="6" className="text-center">
                          No scans found
                        </td>
                      </tr>
                    ) : (
                      scans.map((scan, index) => {
                        // vipMemberId is now an object with _id, name, email, position
                        const vipMember = scan.vipMemberId || {};
                        const allTasks = [
                          ...(scan.pendingTasks || []),
                          ...(scan.completedTasks || []),
                          ...(scan.failedTasks || []),
                        ];
                        return (
                          <tr key={scan._id || index}>
                            <td>{scan._id || scan.id || "N/A"}</td>
                            <td>{vipMember._id || "N/A"}</td>
                            <td>{vipMember.name || "N/A"}</td>
                            <td>
                              {allTasks.length > 0
                                ? allTasks.join(", ")
                                : "N/A"}
                            </td>
                            <td>
                              <span
                                className={`badge ${
                                  scan.status === "completed"
                                    ? "badge-success"
                                    : scan.status === "failed"
                                      ? "badge-danger"
                                      : "badge-warning"
                                }`}
                              >
                                {scan.status || "pending"}
                              </span>
                            </td>
                            <td>
                              {scan.createdAt
                                ? new Date(scan.createdAt).toLocaleString()
                                : "N/A"}
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalCount > 0 && (
                <div
                  className="d-flex flex-column flex-md-row justify-content-between align-items-center mt-3 pt-3"
                  style={{ borderTop: "1px solid #e9ecef", gap: "12px" }}
                >
                  <div className="dataTables_paginate">
                    {totalCount > pageSize && (
                      <Pagination
                        className="pagination-bar"
                        currentPage={currentPage}
                        totalCount={totalCount}
                        pageSize={pageSize}
                        onPageChange={(page) => setCurrentPage(page)}
                      />
                    )}
                  </div>
                  {totalCount > 10 && (
                    <div className="d-flex align-items-center gap-2">
                      <label
                        className="mb-0 text-muted"
                        style={{ fontSize: "12px" }}
                      >
                        Show:
                      </label>
                      <select
                        value={pageSize}
                        onChange={(e) => {
                          setPageSize(parseInt(e.target.value));
                          setCurrentPage(1);
                        }}
                        className="form-control form-control-sm"
                        style={{
                          width: "auto",
                          minWidth: "80px",
                          fontSize: "12px",
                        }}
                      >
                        <option value={10}>10</option>
                        <option value={25}>25</option>
                        <option value={50}>50</option>
                        <option value={100}>100</option>
                      </select>
                      <span className="text-muted" style={{ fontSize: "12px" }}>
                        per page
                      </span>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Create Scan Modal */}
      {showCreateScanModal && (
        <div
          className="modal fade show"
          style={{ display: "block" }}
          tabIndex="-1"
        >
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  Initiate Executive Monitoring Scan
                </h5>
                <button
                  type="button"
                  className="close"
                  onClick={() => {
                    setShowCreateScanModal(false);
                    setNewScan({ vipMemberId: "", modules: [] });
                  }}
                >
                  <span>&times;</span>
                </button>
              </div>
              <div className="modal-body">
                <div className="form-group">
                  <label>VIP Member *</label>
                  <select
                    className="form-control"
                    value={newScan.vipMemberId}
                    onChange={(e) =>
                      setNewScan({ ...newScan, vipMemberId: e.target.value })
                    }
                    required
                  >
                    <option value="">Select VIP Member</option>
                    {vipMembers.map((member) => (
                      <option
                        key={member._id || member.id}
                        value={member._id || member.id}
                      >
                        {member.name} ({member.email})
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Modules * (Select at least one)</label>
                  <div className="mt-2">
                    {availableModules.map((module) => (
                      <div key={module.value} className="form-check">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          checked={newScan.modules.includes(module.value)}
                          onChange={() => toggleModule(module.value)}
                          id={`module-${module.value}`}
                        />
                        <label
                          className="form-check-label"
                          htmlFor={`module-${module.value}`}
                        >
                          {module.label}
                        </label>
                      </div>
                    ))}
                  </div>
                  {newScan.modules.length > 0 && (
                    <div className="mt-2">
                      <small className="text-muted">
                        Selected: {newScan.modules.join(", ")}
                      </small>
                    </div>
                  )}
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowCreateScanModal(false);
                    setNewScan({ vipMemberId: "", modules: [] });
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={createScan}
                  disabled={
                    !newScan.vipMemberId || newScan.modules.length === 0
                  }
                >
                  Initiate Scan
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal Backdrop */}
      {showCreateScanModal && (
        <div
          className="modal-backdrop fade show"
          onClick={() => {
            setShowCreateScanModal(false);
            setNewScan({ vipMemberId: "", modules: [] });
          }}
        ></div>
      )}
    </div>
  );
};

export default Scans;
